import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientContext = createContext(null);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within ClientProvider");
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, loading: authLoading, user } = useAuth();

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      const fetchedClients = response.data;
      setClients(fetchedClients);
      
      // Restore selected client from localStorage only if it exists in accessible clients
      const savedClientId = localStorage.getItem("selectedClientId");
      if (savedClientId) {
        const saved = fetchedClients.find(c => c.id === savedClientId);
        if (saved) {
          setSelectedClient(saved);
        } else if (fetchedClients.length > 0) {
          // Saved client not accessible, select first available
          setSelectedClient(fetchedClients[0]);
          localStorage.setItem("selectedClientId", fetchedClients[0].id);
        } else {
          // No accessible clients
          setSelectedClient(null);
          localStorage.removeItem("selectedClientId");
        }
      } else if (fetchedClients.length > 0) {
        setSelectedClient(fetchedClients[0]);
        localStorage.setItem("selectedClientId", fetchedClients[0].id);
      } else {
        setSelectedClient(null);
      }
    } catch (err) {
      setClients([]);
      setSelectedClient(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch clients only after auth is initialized and user is authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchClients();
    } else if (!authLoading && !isAuthenticated) {
      // Not authenticated - clear clients
      setClients([]);
      setSelectedClient(null);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, user?.id, fetchClients]);

  const selectClient = useCallback((client) => {
    setSelectedClient(client);
    if (client) {
      localStorage.setItem("selectedClientId", client.id);
    } else {
      localStorage.removeItem("selectedClientId");
    }
  }, []);

  const refreshClients = useCallback(() => {
    if (isAuthenticated) {
      fetchClients();
    }
  }, [isAuthenticated, fetchClients]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    clients,
    selectedClient,
    selectClient,
    loading: loading || authLoading,
    refreshClients,
    clientId: selectedClient?.id || null
  }), [clients, selectedClient, selectClient, loading, authLoading, refreshClients]);

  return (
    <ClientContext.Provider value={contextValue}>
      {children}
    </ClientContext.Provider>
  );
};

export default ClientContext;
